import { localeNames } from 'common/constants/localeTypes'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import {
  deleteWebsitePageByIdRequest,
  deleteWebsitePageSetRequest,
} from 'src/editor/api/website-pages-api'
import { ButtonTypeEnum } from 'src/editor/shared/ui-lib/buttons'
import { OptionsDialogWindow } from 'src/editor/shared/ui-lib/modal-window/dialog-window/options-dialog-window'
import { WarningDialogWindow } from 'src/editor/shared/ui-lib/modal-window/dialog-window/warning-dialog-window'
import { WebsitePageLocale } from 'src/shared/types/website-page-interface'

export interface DeletePageModalProps {
  websiteId: number
  websitePageSetId: number
  websitePageId: number
  pageName?: string
  isOpened: boolean
  onClose: () => void
  locales: WebsitePageLocale[]
  websitePageLocale: PageLocaleEnum
}

const DELETE_CURRENT_PAGE_OPTION_ID = 1
const DELETE_ALL_PAGES_OPTION_ID = 2

export const DeletePageModal = ({
  websiteId,
  websitePageSetId,
  websitePageId,
  pageName,
  isOpened,
  onClose,
  locales,
  websitePageLocale,
}: DeletePageModalProps) => {
  const { t } = useTranslation()
  const [activeOptionId, setActiveOptionId] = useState<number | null>(null)
  const { fetcher: fetcherPageSet, isLoading: isLoadingPageSet } =
    useMutationWithValidation({
      requestFunction: deleteWebsitePageSetRequest,
      successToastMessage: t(
        'website_settings.sidebar.pages_management.delete_page.removed_successfully',
      ),
      handleFinally: onClose,
      setQueryDataArgsOnSuccess: websitePagesQuery.setQueryDataArgs(
        [websiteId],
        prevData => ({
          ...prevData,
          pages: prevData.pages.filter(
            websitePageSet => websitePageSet.id !== websitePageSetId,
          ),
        }),
      ),
    })
  const { fetcher: fetcherPage, isLoading: isLoadingPage } =
    useMutationWithValidation({
      requestFunction: deleteWebsitePageByIdRequest,
      successToastMessage: t(
        'website_settings.sidebar.pages_management.delete_page.removed_successfully',
      ),
      handleFinally: onClose,
      setQueryDataArgsOnSuccess: websitePagesQuery.setQueryDataArgs(
        [websiteId],
        prevData => ({
          ...prevData,
          pages: prevData.pages.map(pageSet =>
            pageSet.id === websitePageSetId
              ? {
                  ...pageSet,
                  locales: (pageSet.locales ?? []).filter(
                    p => p.id !== websitePageId,
                  ),
                }
              : pageSet,
          ),
        }),
      ),
    })

  const handleRemovePage = () => {
    fetcherPage({ websitePageId })
  }
  const handleRemovePageSet = () => {
    fetcherPageSet({ websitePageSetId })
  }

  const handleConfirmButton = () => {
    if (activeOptionId === DELETE_CURRENT_PAGE_OPTION_ID) {
      handleRemovePage()
    } else {
      handleRemovePageSet()
    }
  }

  return locales.length > 1 ? (
    <OptionsDialogWindow
      isOpened={isOpened}
      onClose={onClose}
      title={t('website_settings.sidebar.pages_management.delete_page.title')}
      description={t(
        'website_settings.sidebar.pages_management.delete_page.multiple_lang_description',
      )}
      cancelButtonProps={{
        children: t('global.cancel'),
        onClick: onClose,
        type: ButtonTypeEnum.Secondary,
      }}
      confirmButtonProps={{
        isLoading: isLoadingPageSet || isLoadingPage,
        onClick: handleConfirmButton,
        children: t('components.core.actions.remove'),
        disabled: !activeOptionId,
      }}
      activeOptionId={activeOptionId}
      onChangeActiveOptionId={setActiveOptionId}
      options={[
        {
          id: DELETE_CURRENT_PAGE_OPTION_ID,
          label: t(
            'website_settings.sidebar.pages_management.delete_page.from_current_language_icu',
            {
              pageLocale: t(localeNames[websitePageLocale]),
            },
          ),
        },
        {
          id: DELETE_ALL_PAGES_OPTION_ID,
          label: t(
            'website_settings.sidebar.pages_management.delete_page.from_all_languages',
          ),
        },
      ]}
    />
  ) : (
    <WarningDialogWindow
      isOpened={isOpened}
      onClose={onClose}
      title={t('website_settings.sidebar.pages_management.delete_page.title')}
      description={t(
        'website_settings.sidebar.pages_management.delete_page.description_icu',
        { pageName },
      )}
      cancelButtonProps={{
        children: t('global.cancel'),
        onClick: onClose,
      }}
      confirmButtonProps={{
        isLoading: isLoadingPageSet,
        onClick: handleRemovePage,
        children: t('components.core.actions.remove'),
      }}
    />
  )
}
